import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import detailService from '../../services/detailService';
import championshipService from '../../services/championshipService';
import './MatchDetails.css';

const translatePhaseToPortuguese = (phase) => {
  const phases = {
    'eighthFinals': 'Oitavas de Final',
    'quarterFinals': 'Quartas de Final',
    'semiFinals': 'Semifinal',
    'thirdPlace': 'Disputa de 3º Lugar',
    'final': 'Final',
    'group': 'Fase de Grupos',
    'roundOf16': 'Oitavas de Final',
    'roundOf32': '32ªs de Final'
  };
  return phases[phase] || phase;
};

const getTeamLogoUrl = (badge) => {
  if (badge?.startsWith('http') || badge?.startsWith('/')) {
    return badge;
  }
  return '/default-team-badge.png';
};

const formatDate = (dateString) => {
  if (!dateString) return 'Data não definida';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Data inválida';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const MatchDetails = () => {
  const { matchId } = useParams();
  const { user, loadingAuth } = useAuth();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [players, setPlayers] = useState({ home: [], away: [] });
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    player: null,
    teamSide: null
  });
  const [modalInput, setModalInput] = useState({
    minute: '',
    reason: '',
    goalType: 'normal',
    cardType: 'yellow'
  });
  const [isSaving, setIsSaving] = useState(false);
  
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [tempScore, setTempScore] = useState({ home: 0, away: 0 });

  const [isEditingPenalties, setIsEditingPenalties] = useState(false);
  const [tempPenalties, setTempPenalties] = useState({ home: 0, away: 0 });

  // ESTADOS PARA EDIÇÃO DE DATA E HORA
  const [isEditingDateTime, setIsEditingDateTime] = useState(false);
  const [tempDate, setTempDate] = useState('');
  const [tempTime, setTempTime] = useState('');

  // ESTADOS PARA EDIÇÃO DE LOCAL
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [tempLocation, setTempLocation] = useState('');

  const canManageMatchDetails = useMemo(() => {
    return !loadingAuth && user && user.role && ['admin', 'gestor', 'diretor', 'técnico'].includes(user.role.toLowerCase());
  }, [user, loadingAuth]);

  const fetchMatchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await detailService.getMatchDetails(matchId);
      setMatch(data);

      const processPlayers = (lineup) => lineup?.map(item => ({
        id: item.player?._id,
        name: item.player?.name || 'Jogador desconhecido',
        shirtNumber: item.shirtNumber,
        position: item.position,
        suspended: item.suspended || false
      })) || [];

      setPlayers({
        home: processPlayers(data.lineups?.teamA),
        away: processPlayers(data.lineups?.teamB)
      });

      setTempScore({
        home: data.scoreA !== null ? data.scoreA : 0,
        away: data.scoreB !== null ? data.scoreB : 0
      });
      
      setTempPenalties({
        home: data.penaltiesA !== null ? data.penaltiesA : 0,
        away: data.penaltiesB !== null ? data.penaltiesB : 0
      });

      if (data.date) {
          const matchDate = new Date(data.date);
          setTempDate(matchDate.toISOString().split('T')[0]);
          setTempTime(matchDate.toTimeString().substring(0, 5));
      } else {
          setTempDate('');
          setTempTime('');
      }

      setTempLocation(data.location || '');

    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar detalhes da partida:', err);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (!loadingAuth) {
      fetchMatchData();
    }
  }, [loadingAuth, fetchMatchData]);

  // Função auxiliar para acionar a verificação de suspensões no backend
  const runSuspensionCheck = async () => {
    if (!canManageMatchDetails) return;
    try {
      setIsSaving(true);
      console.log('Acionando verificação de suspensões no backend...');
      await new Promise(resolve => setTimeout(resolve, 500));
      await detailService.triggerSuspensionCheck(matchId);
      console.log('Verificação de suspensões acionada com sucesso no backend (campeonato específico).');
      await fetchMatchData();
      alert('Suspensões reavaliadas com sucesso!');
    } catch (err) {
      console.error('Falha ao acionar verificação de suspensões:', err);
      alert('Erro ao verificar suspensões: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncLineups = async () => {
    if (!canManageMatchDetails) return;

    if (window.confirm('Deseja atualizar as escalações com os jogadores mais recentes dos times? Isso substituirá a lista de jogadores atual desta partida.')) {
      try {
        setIsSaving(true);
        await detailService.syncMatchLineups(matchId);
        await fetchMatchData();
        alert('Escalações atualizadas com sucesso!');
      } catch (err) {
        console.error('Erro ao sincronizar escalações:', err);
        alert('Erro ao sincronizar escalações: ' + (err.response?.data?.message || err.message));
      } finally {
        setIsSaving(false);
      }
    }
  };
  
  const penaltiesAreSet = match && typeof match.penaltiesA === 'number' && typeof match.penaltiesB === 'number';

  const canAddPenalties = useMemo(() => {
    if (!match || !canManageMatchDetails) return false;
    const isKnockout = match.phase && match.phase !== 'group';
    const isDraw = match.scoreA === match.scoreB;
    const isScheduledOrCompleted = match.status === 'scheduled' || match.status === 'completed';
    return isKnockout && isDraw && isScheduledOrCompleted && !penaltiesAreSet;
  }, [match, canManageMatchDetails, penaltiesAreSet]);

  const renderMatchStageInfo = (matchData) => {
    if (!matchData) return null;
    
    if (matchData.phase === 'group') {
        let info = `Rodada: ${matchData.round}`;
        if (matchData.group) {
            info += ` (Grupo ${matchData.group})`;
        }
        return <span className="match-phase-info"> | <strong>{translatePhaseToPortuguese(matchData.phase)}</strong> - {info}</span>;
    } 
    else if (matchData.phase) {
        return <span className="match-phase-info"> | <strong>Fase:</strong> {translatePhaseToPortuguese(matchData.phase)}</span>;
    }
    else if (matchData.round) {
      return <span className="match-phase-info"> | <strong>Rodada:</strong> {matchData.round}</span>;
    }
    return null;
  };

  const handleTeamImageError = (e) => {
    e.target.src = '/default-team-badge.png';
    e.target.onerror = null;
  };

  const handleInlineStatusChange = async (e) => {
    if (!canManageMatchDetails) return;
    const newStatus = e.target.value;
    const originalStatus = match.status;
    setMatch(prev => ({ ...prev, status: newStatus }));
    try {
      await championshipService.updateMatch(matchId, { status: newStatus });
      alert('Status atualizado com sucesso!');
      if (newStatus === 'completed') {
        await runSuspensionCheck(); // Mantido apenas quando finaliza a partida
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status: ' + (error.response?.data?.message || error.message));
      setMatch(prev => ({ ...prev, status: originalStatus }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetMatch = async () => {
    if (!canManageMatchDetails) return;
    if (window.confirm('Tem certeza que deseja resetar todos os dados desta partida? Esta ação é irreversível.')) {
      try {
        setIsSaving(true);
        await detailService.resetMatch(matchId);
        await fetchMatchData();
        await runSuspensionCheck(); // Mantido para reavaliar suspensões após reset
        alert('Partida resetada com sucesso!');
      } catch (err) {
        console.error('Erro ao resetar partida:', err);
        alert('Erro ao resetar partida: ' + (err.response?.data?.message || err.message));
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleOpenModal = (player, teamSide, type, preselectedCardType = 'yellow') => {
    setModalState({
      isOpen: true,
      type,
      player,
      teamSide
    });
    setModalInput({
      minute: '',
      reason: '',
      goalType: 'normal',
      cardType: preselectedCardType
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      player: null,
      teamSide: null
    });
  };

  const handleAddGoal = async () => {
    if (!modalState.player || !modalState.teamSide || !match) return;

    try {
      setIsSaving(true);
      const teamId = modalState.teamSide === 'home' ? match.teamA._id : match.teamB._id;

      // Atualização otimista do estado
      setMatch(prev => {
        const newScoreA = modalInput.goalType !== 'contra' ? 
                          (modalState.teamSide === 'home' ? (prev.scoreA || 0) + 1 : prev.scoreA) :
                          (modalState.teamSide === 'away' ? (prev.scoreA || 0) + 1 : prev.scoreA);
        
        const newScoreB = modalInput.goalType !== 'contra' ?
                          (modalState.teamSide === 'away' ? (prev.scoreB || 0) + 1 : prev.scoreB) :
                          (modalState.teamSide === 'home' ? (prev.scoreB || 0) + 1 : prev.scoreB);

        return {
          ...prev,
          scoreA: newScoreA,
          scoreB: newScoreB,
          goals: [
            ...(prev.goals || []),
            {
              _id: `temp_${Date.now()}`,
              player: { _id: modalState.player.id, name: modalState.player.name },
              team: teamId,
              type: modalInput.goalType,
              minute: modalInput.minute.trim()
            }
          ]
        };
      });

      await detailService.addGoal(matchId, {
        player: modalState.player.id,
        team: teamId,
        type: modalInput.goalType,
        minute: modalInput.minute.trim()
      });

      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao adicionar gol:', error);
      alert('Erro ao adicionar gol: ' + (error.response?.data?.message || error.message));
      await fetchMatchData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCard = async () => {
    if (!modalState.player || !modalState.teamSide || !match) return;

    try {
      setIsSaving(true);
      const teamId = modalState.teamSide === 'home' ? match.teamA._id : match.teamB._id;

      await detailService.addCard(matchId, {
        player: modalState.player.id,
        team: teamId,
        type: modalInput.cardType,
        minute: modalInput.minute.trim(),
        reason: modalInput.reason.trim()
      });

      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao adicionar cartão:', error);
      alert('Erro ao adicionar cartão: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveGoal = async (goalId) => {
    if (!canManageMatchDetails) return;
    if (!window.confirm('Deseja realmente remover este gol?')) return;

    try {
      setIsSaving(true);
      await detailService.removeGoal(matchId, goalId);
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
    } catch (error) {
      console.error('Erro ao remover gol:', error);
      alert('Erro ao remover gol: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCard = async (cardId) => {
    if (!canManageMatchDetails) return;
    if (!window.confirm('Deseja realmente remover este cartão?')) return;

    try {
      setIsSaving(true);
      await detailService.removeCard(matchId, cardId);
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
    } catch (error) {
      console.error('Erro ao remover cartão:', error);
      alert('Erro ao remover cartão: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePlayer = async (playerId, teamSide) => {
    if (!canManageMatchDetails) return;
    if (!window.confirm(`Deseja realmente remover este jogador da escalação?`)) return;

    try {
      setIsSaving(true);
      const teamLetter = teamSide === 'home' ? 'A' : 'B';
      await detailService.removePlayerFromLineup(matchId, teamLetter, playerId);
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      alert('Jogador removido da escalação com sucesso!');
    } catch (error) {
      console.error('Erro ao remover jogador:', error);
      alert('Erro ao remover jogador: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSuspension = async (playerId, teamSide) => {
    if (!canManageMatchDetails) return;

    try {
      setIsSaving(true);
      const teamLetter = teamSide === 'home' ? 'A' : 'B';
      await detailService.togglePlayerSuspension(matchId, teamLetter, playerId);
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
    } catch (error) {
      console.error('Erro ao alternar suspensão:', error);
      alert('Erro ao alternar suspensão: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditingScore = () => setIsEditingScore(true);

  const handleCancelEditingScore = () => {
    setIsEditingScore(false);
    if (match) {
      setTempScore({
        home: match.scoreA !== null ? match.scoreA : 0,
        away: match.scoreB !== null ? match.scoreB : 0
      });
    }
  };

  const handleSaveScore = async () => {
    if (!canManageMatchDetails || !match) return;
    setIsSaving(true);
    try {
      await championshipService.updateMatch(matchId, {
        scoreA: tempScore.home,
        scoreB: tempScore.away
      });
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      setIsEditingScore(false);
      alert('Placar atualizado com sucesso!');
    } catch (error) {
      console.error("Erro ao atualizar placar:", error);
      alert("Erro ao atualizar placar: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleScoreChange = (team, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0) return;
    setTempScore(prev => ({ ...prev, [team]: numValue }));
  };
  
  const handleStartEditingPenalties = () => setIsEditingPenalties(true);

  const handleCancelEditingPenalties = () => {
    setIsEditingPenalties(false);
    if (match) {
      setTempPenalties({
        home: match.penaltiesA !== null ? match.penaltiesA : 0,
        away: match.penaltiesB !== null ? match.penaltiesB : 0
      });
    }
  };

  const handlePenaltyChange = (team, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0) return;
    setTempPenalties(prev => ({ ...prev, [team]: numValue }));
  };

  const handleSavePenalties = async () => {
    if (!canManageMatchDetails || !match) return;
    setIsSaving(true);
    try {
      await championshipService.updateMatch(matchId, {
        penaltiesA: tempPenalties.home,
        penaltiesB: tempPenalties.away
      });
      alert('Placar de pênaltis atualizado com sucesso!');
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      setIsEditingPenalties(false);
    } catch (error) {
      console.error("Erro ao atualizar pênaltis:", error);
      alert("Erro ao atualizar pênaltis: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditingDateTime = () => setIsEditingDateTime(true);

  const handleCancelEditingDateTime = () => {
    setIsEditingDateTime(false);
    if (match && match.date) {
      const matchDate = new Date(match.date);
      setTempDate(matchDate.toISOString().split('T')[0]);
      setTempTime(matchDate.toTimeString().substring(0, 5));
    } else {
      setTempDate('');
      setTempTime('');
    }
  };

  const handleSaveDateTime = async () => {
    if (!canManageMatchDetails || !match || !tempDate || !tempTime) return;
    setIsSaving(true);
    try {
        const newDateTime = new Date(`${tempDate}T${tempTime}`);
        await championshipService.updateMatch(matchId, { date: newDateTime.toISOString() });
        alert('Data e hora atualizadas com sucesso!');
        await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
        setIsEditingDateTime(false);
    } catch (error) {
        console.error("Erro ao atualizar data e hora:", error);
        alert("Erro ao atualizar data e hora: " + (error.response?.data?.message || error.message));
    } finally {
        setIsSaving(false);
    }
  };

  const handleStartEditingLocation = () => setIsEditingLocation(true);

  const handleCancelEditingLocation = () => {
    setIsEditingLocation(false);
    setTempLocation(match.location || '');
  };

  const handleSaveLocation = async () => {
    if (!canManageMatchDetails || !match) return;
    setIsSaving(true);
    try {
      await championshipService.updateMatch(matchId, { location: tempLocation.trim() });
      alert('Local atualizado com sucesso!');
      await fetchMatchData(); // Apenas atualiza os dados, sem reavaliar suspensões
      setIsEditingLocation(false);
    } catch (error) {
      console.error("Erro ao atualizar local:", error);
      alert("Erro ao atualizar local: " + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingAuth || (loading && !match)) return <div className="loading">Carregando partida...</div>;
  if (error) return <div className="error">Erro ao carregar partida: {error}</div>;
  if (!match && !loadingAuth && !loading) return <div className="error">Partida não encontrada</div>;

  const golsCasa = match.goals?.filter(g => g.team === match.teamA?._id) || [];
  const golsVisitante = match.goals?.filter(g => g.team === match.teamB?._id) || [];
  const cartoesCasa = match.cards?.filter(c => c.team === match.teamA?._id) || [];
  const cartoesVisitante = match.cards?.filter(c => c.team === match.teamB?._id) || [];

  const playerRoles = ['goleiro', 'zagueiro', 'lateral', 'volante', 'meia', 'atacante'];
  const coachRoles = ['técnico', 'tecnico'];
  const assistantRoles = ['auxiliar técnico', 'aux. técnico'];
  const directorRoles = ['diretor'];

  return (
    <div className="match-details-container">
      <div className="match-header">
        <div className="team">
          <div className="team-badge-container">
            <img
              src={getTeamLogoUrl(match.teamA?.badge)}
              alt={match.teamA?.name || 'Time Casa'}
              className="team-badge"
              onError={handleTeamImageError}
            />
          </div>
          <h2>{match.teamA?.name || 'Time Casa'}</h2>
        </div>

        <div className="match-score-container">
          {isEditingScore ? (
            <div className="score-editor">
              <input
                type="number"
                min="0"
                value={tempScore.home}
                onChange={(e) => handleScoreChange('home', e.target.value)}
                className="score-input"
              />
              <span>x</span>
              <input
                type="number"
                min="0"
                value={tempScore.away}
                onChange={(e) => handleScoreChange('away', e.target.value)}
                className="score-input"
              />
              <div className="score-edit-actions">
                <button onClick={handleSaveScore} disabled={isSaving} className="small-button">
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
                <button onClick={handleCancelEditingScore} disabled={isSaving} className="small-button cancel">
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="match-score">
                <span>{match.scoreA !== null ? match.scoreA : '-'}</span>
                <span>x</span>
                <span>{match.scoreB !== null ? match.scoreB : '-'}</span>
                {canManageMatchDetails && (
                  <button
                    onClick={handleStartEditingScore}
                    className="edit-score-button"
                    title="Editar placar manualmente"
                    disabled={isSaving}
                  >
                    ✏️
                  </button>
                )}
              </div>
              
              <div className="penalties-score-container">
                {isEditingPenalties ? (
                    <div className="score-editor penalties-editor">
                        <input
                        type="number"
                        min="0"
                        value={tempPenalties.home}
                        onChange={(e) => handlePenaltyChange('home', e.target.value)}
                        className="score-input penalty-input"
                        />
                        <span>x</span>
                        <input
                        type="number"
                        min="0"
                        value={tempPenalties.away}
                        onChange={(e) => handlePenaltyChange('away', e.target.value)}
                        className="score-input penalty-input"
                        />
                        <div className="score-edit-actions">
                        <button onClick={handleSavePenalties} disabled={isSaving} className="small-button">
                            {isSaving ? '...' : 'Salvar'}
                        </button>
                        <button onClick={handleCancelEditingPenalties} disabled={isSaving} className="small-button cancel">
                            X
                        </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {penaltiesAreSet && (
                            <div className="penalties-score">
                                <span>({match.penaltiesA} - {match.penaltiesB} nos pênaltis)</span>
                                {canManageMatchDetails && (
                                    <button onClick={handleStartEditingPenalties} className="edit-score-button" title="Editar Pênaltis">✏️</button>
                                )}
                            </div>
                        )}
                        {canAddPenalties && (
                            <button onClick={handleStartEditingPenalties} className="action-button add-penalties-button">
                                Adicionar Pênaltis
                            </button>
                        )}
                    </>
                )}
              </div>

              <div className="match-info">
                  {isEditingDateTime ? (
                      <div className="datetime-editor">
                          <input
                              type="date"
                              value={tempDate}
                              onChange={(e) => setTempDate(e.target.value)}
                              className="date-input"
                          />
                          <input
                              type="time"
                              value={tempTime}
                              onChange={(e) => setTempTime(e.target.value)}
                              className="time-input"
                          />
                          <div className="datetime-edit-actions">
                              <button onClick={handleSaveDateTime} disabled={isSaving} className="small-button">
                                  {isSaving ? 'Salvando...' : 'Salvar'}
                              </button>
                              <button onClick={handleCancelEditingDateTime} disabled={isSaving} className="small-button cancel">
                                  Cancelar
                              </button>
                          </div>
                      </div>
                  ) : (
                      <p className="match-date">
                          {formatDate(match.date)}
                          {canManageMatchDetails && (
                              <button
                                  onClick={handleStartEditingDateTime}
                                  className="edit-datetime-button"
                                  title="Editar data e hora"
                                  disabled={isSaving}
                              >
                                  ✏️
                              </button>
                          )}
                      </p>
                  )}
                  <div className="match-status-inline">
                    {canManageMatchDetails ? (
                      <select
                        value={match.status || 'scheduled'}
                        onChange={handleInlineStatusChange}
                        disabled={isSaving}
                      >
                        <option value="scheduled">Agendada</option>
                        <option value="in_progress">Em Andamento</option>
                        <option value="completed">Finalizada</option>
                        <option value="postponed">Adiada</option>
                        <option value="canceled">Cancelada</option>
                      </select>
                    ) : (
                      <span className="status-display">
                        {match.status === 'scheduled' && 'Agendada'}
                        {match.status === 'in_progress' && 'Em Andamento'}
                        {match.status === 'completed' && 'Finalizada'}
                        {match.status === 'postponed' && 'Adiada'}
                        {match.status === 'canceled' && 'Cancelada'}
                      </span>
                    )}
                  </div>
              </div>
            </>
          )}
        </div>

        <div className="team">
          <div className="team-badge-container">
            <img
              src={getTeamLogoUrl(match.teamB?.badge)}
              alt={match.teamB?.name || 'Time Visitante'}
              className="team-badge"
              onError={handleTeamImageError}
            />
          </div>
          <h2>{match.teamB?.name || 'Time Visitante'}</h2>
        </div>
      </div>

      <div className="match-venue">
          {isEditingLocation ? (
              <div className="location-editor">
                  <input
                      type="text"
                      value={tempLocation}
                      onChange={(e) => setTempLocation(e.target.value)}
                      className="location-input"
                      placeholder="Local da partida"
                  />
                  <div className="location-edit-actions">
                      <button onClick={handleSaveLocation} disabled={isSaving} className="small-button">
                          {isSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button onClick={handleCancelEditingLocation} disabled={isSaving} className="small-button cancel">
                          Cancelar
                      </button>
                  </div>
              </div>
          ) : (
              <p>
                  <strong>Local:</strong> {match.location || 'Local não definido'}
                  {canManageMatchDetails && (
                      <button
                          onClick={handleStartEditingLocation}
                          className="edit-location-button"
                          title="Editar local"
                          disabled={isSaving}
                      >
                          ✏️
                      </button>
                  )}
                  {renderMatchStageInfo(match)}
              </p>
          )}
      </div>

      <div className="quick-actions">
        {canManageMatchDetails && (
          <>
            <button
              className="action-button"
              style={{ backgroundColor: '#0284c7' }}
              onClick={handleSyncLineups}
              disabled={isSaving}
              title="Atualiza as listas de jogadores com os elencos atuais dos times"
            >
              Atualizar Escalações
            </button>
            <button
              className="action-button"
              style={{ backgroundColor: '#059669' }}
              onClick={runSuspensionCheck}
              disabled={isSaving}
              title="Reavalia as suspensões de todos os jogadores deste campeonato."
            >
              Verificar Suspensões
            </button>
            <button
              className="action-button goal-button"
              onClick={() => handleOpenModal(null, 'home', 'goal')}
              disabled={isSaving}
            >
              Adicionar Gol
            </button>
            <button
              className="action-button card-button"
              onClick={() => handleOpenModal(null, 'home', 'card')}
              disabled={isSaving}
            >
              Adicionar Cartão
            </button>
            <button
              className="action-button reset-button"
              onClick={handleResetMatch}
              disabled={isSaving}
            >
              Resetar Partida
            </button>
          </>
        )}
        <Link
          to={`/partida/${matchId}/sumula`}
          target="_blank"
          className="action-button"
          style={isSaving ? { pointerEvents: 'none', opacity: 0.65, backgroundColor: '#64748b' } : { backgroundColor: '#475569' }}
        >
          Abrir Súmula
        </Link>
      </div>

      <div className="match-details-grid">
        <div className="team-events-column">
          <div className="detail-section">
            <h3>Gols - {match.teamA?.name}</h3>
            <ul>
              {golsCasa.length > 0 ?
                golsCasa
                  .sort((a, b) => (parseInt(String(a.minute).split('+')[0]) || 0) - (parseInt(String(b.minute).split('+')[0]) || 0))
                  .map((g) => (
                    <li key={g._id}>
                      <span className="player">
                        {g.player?.name}
                        {g.type === 'contra' && ' (Contra)'}
                        {g.minute && ` (${g.minute}')`}
                      </span>
                      {canManageMatchDetails && (
                        <button
                          className="delete-goal-button"
                          onClick={() => handleRemoveGoal(g._id)}
                          title="Remover gol"
                          disabled={isSaving}
                        >
                          🗑️
                        </button>
                      )}
                    </li>
                  )) :
                <li>Nenhum gol marcado.</li>
              }
            </ul>
          </div>
          <div className="detail-section">
            <h3>Cartões - {match.teamA?.name}</h3>
            <ul>
              {cartoesCasa.length > 0 ?
                cartoesCasa
                  .sort((a, b) => (parseInt(String(a.minute).split('+')[0]) || 0) - (parseInt(String(b.minute).split('+')[0]) || 0))
                  .map(c => (
                    <li key={c._id}>
                      <span className={`card-icon ${c.type}`}>
                        {c.type === 'yellow' ? '🟨' : '🟥'}
                      </span>
                      <span className="player">
                        {c.player?.name}
                        {c.minute && ` (${c.minute}')`}
                      </span>
                      {canManageMatchDetails && (
                        <button
                          className="delete-goal-button"
                          onClick={() => handleRemoveCard(c._id)}
                          title="Remover cartão"
                          disabled={isSaving}
                        >
                          🗑️
                        </button>
                      )}
                    </li>
                  )) :
                <li>Nenhum cartão aplicado.</li>
              }
            </ul>
          </div>
        </div>

        <div className="team-events-column">
          <div className="detail-section">
            <h3>Gols - {match.teamB?.name}</h3>
            <ul>
              {golsVisitante.length > 0 ?
                golsVisitante
                  .sort((a, b) => (parseInt(String(a.minute).split('+')[0]) || 0) - (parseInt(String(b.minute).split('+')[0]) || 0))
                  .map((g) => (
                    <li key={g._id}>
                      <span className="player">
                        {g.player?.name}
                        {g.type === 'contra' && ' (Contra)'}
                        {g.minute && ` (${g.minute}')`}
                      </span>
                      {canManageMatchDetails && (
                        <button
                          className="delete-goal-button"
                          onClick={() => handleRemoveGoal(g._id)}
                          title="Remover gol"
                          disabled={isSaving}
                        >
                          🗑️
                        </button>
                      )}
                    </li>
                  )) :
                <li>Nenhum gol marcado.</li>
              }
            </ul>
          </div>
          <div className="detail-section">
            <h3>Cartões - {match.teamB?.name}</h3>
            <ul>
              {cartoesVisitante.length > 0 ?
                cartoesVisitante
                  .sort((a, b) => (parseInt(String(a.minute).split('+')[0]) || 0) - (parseInt(String(b.minute).split('+')[0]) || 0))
                  .map(c => (
                    <li key={c._id}>
                      <span className={`card-icon ${c.type}`}>
                        {c.type === 'yellow' ? '🟨' : '🟥'}
                      </span>
                      <span className="player">
                        {c.player?.name}
                        {c.minute && ` (${c.minute}')`}
                      </span>
                      {canManageMatchDetails && (
                        <button
                          className="delete-goal-button"
                          onClick={() => handleRemoveCard(c._id)}
                          title="Remover cartão"
                          disabled={isSaving}
                        >
                          🗑️
                        </button>
                      )}
                    </li>
                  )) :
                <li>Nenhum cartão aplicado.</li>
              }
            </ul>
          </div>
        </div>
      </div>

      <div className="lineups-section">
        <h3>
          Escalações
          {canManageMatchDetails &&
            <small>(Clique nos ícones para adicionar eventos)</small>
          }
        </h3>
        <div className="lineups-container">
          {['home', 'away'].map(teamSide => {
            const currentTeam = teamSide === 'home' ? match.teamA : match.teamB;
            const teamPlayersFullList = teamSide === 'home' ? players.home : players.away;

            const playersOnly = teamPlayersFullList.filter(p => playerRoles.includes(p.position?.toLowerCase()));
            const coaches = teamPlayersFullList.filter(p => coachRoles.includes(p.position?.toLowerCase()));
            const assistants = teamPlayersFullList.filter(p => assistantRoles.includes(p.position?.toLowerCase()));
            const directors = teamPlayersFullList.filter(p => directorRoles.includes(p.position?.toLowerCase()));

            return (
              <div className="team-lineup" key={teamSide}>
                <h4>{currentTeam?.name}</h4>
                {teamPlayersFullList.length > 0 ? (
                  <>
                    {playersOnly.length > 0 && (
                      <ul className="players-list">
                        {playersOnly
                          .sort((a, b) => (a.shirtNumber || Infinity) - (b.shirtNumber || Infinity) || a.name.localeCompare(b.name))
                          .map(p => (
                            <li key={p.id} className={`player-row ${p.suspended ? 'suspended' : ''}`}>
                              <span className="player-info">
                                {p.shirtNumber && `${p.shirtNumber} - `}
                                {p.name}
                                {p.suspended && <span className="suspended-badge"> (SUSPENSO)</span>}
                              </span>
                              {canManageMatchDetails && (
                                <div className="player-actions">
                                  <button
                                    className="action-icon"
                                    onClick={() => handleToggleSuspension(p.id, teamSide)}
                                    title={p.suspended ? `Liberar ${p.name}` : `Suspender ${p.name}`}
                                    disabled={isSaving}
                                  >
                                    🛡️
                                  </button>
                                  <button
                                    className="action-icon remove-player-button"
                                    onClick={() => handleRemovePlayer(p.id, teamSide)}
                                    title={`Remover ${p.name} da escalação`}
                                    disabled={isSaving}
                                  >
                                    ❌
                                  </button>
                                  <button
                                    className="action-icon"
                                    onClick={() => handleOpenModal(p, teamSide, 'goal')}
                                    title={`Adicionar Gol para ${p.name}`}
                                    disabled={isSaving || p.suspended}
                                  >
                                    ⚽
                                  </button>
                                  <button
                                    className="action-icon"
                                    onClick={() => handleOpenModal(p, teamSide, 'card', 'yellow')}
                                    title={`Adicionar Cartão Amarelo para ${p.name}`}
                                    disabled={isSaving || p.suspended}
                                  >
                                    🟨
                                  </button>
                                  <button
                                    className="action-icon"
                                    onClick={() => handleOpenModal(p, teamSide, 'card', 'red')}
                                    title={`Adicionar Cartão Vermelho para ${p.name}`}
                                    disabled={isSaving || p.suspended}
                                  >
                                    🟥
                                  </button>
                                </div>
                              )}
                            </li>
                          ))
                        }
                      </ul>
                    )}
                    
                    {(coaches.length > 0 || assistants.length > 0 || directors.length > 0) && (
                      <div className="staff-info">
                        {coaches.map(c => (
                          <div key={c.id} className="player-row">
                            <span className="player-info">
                              <strong>Técnico:</strong> {c.name}
                            </span>
                            {canManageMatchDetails && (
                              <div className="player-actions">
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(c, teamSide, 'card', 'yellow')}
                                  title={`Adicionar Cartão Amarelo para ${c.name}`}
                                  disabled={isSaving}
                                >
                                  🟨
                                </button>
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(c, teamSide, 'card', 'red')}
                                  title={`Adicionar Cartão Vermelho para ${c.name}`}
                                  disabled={isSaving}
                                >
                                  🟥
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {assistants.map(a => (
                          <div key={a.id} className="player-row">
                            <span className="player-info">
                              <strong>Aux. Técnico:</strong> {a.name}
                            </span>
                            {canManageMatchDetails && (
                              <div className="player-actions">
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(a, teamSide, 'card', 'yellow')}
                                  title={`Adicionar Cartão Amarelo para ${a.name}`}
                                  disabled={isSaving}
                                >
                                  🟨
                                </button>
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(a, teamSide, 'card', 'red')}
                                  title={`Adicionar Cartão Vermelho para ${a.name}`}
                                  disabled={isSaving}
                                >
                                  🟥
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {directors.map(d => (
                          <div key={d.id} className="player-row">
                            <span className="player-info">
                              <strong>Diretor:</strong> {d.name}
                            </span>
                            {canManageMatchDetails && (
                              <div className="player-actions">
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(d, teamSide, 'card', 'yellow')}
                                  title={`Adicionar Cartão Amarelo para ${d.name}`}
                                  disabled={isSaving}
                                >
                                  🟨
                                </button>
                                <button
                                  className="action-icon"
                                  onClick={() => handleOpenModal(d, teamSide, 'card', 'red')}
                                  title={`Adicionar Cartão Vermelho para ${d.name}`}
                                  disabled={isSaving}
                                >
                                  🟥
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : <p>Escalação não disponível.</p>}
              </div>
            );
          })}
        </div>
      </div>

      {modalState.isOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {modalState.type === 'goal' && (
              <>
                <h4>Adicionar Gol</h4>
                {modalState.player && <p><strong>Jogador:</strong> {modalState.player.name}</p>}
                <div className="form-group">
                  <label>Tipo de Gol:</label>
                  <select
                    value={modalInput.goalType}
                    onChange={e => setModalInput({ ...modalInput, goalType: e.target.value })}
                  >
                    <option value="normal">Normal</option>
                    <option value="penalti">Pênalti</option>
                    <option value="falta">Falta</option>
                    <option value="contra">Gol Contra</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Minuto (opcional):</label>
                  <input
                    type="text"
                    value={modalInput.minute}
                    onChange={e => setModalInput({ ...modalInput, minute: e.target.value })}
                    placeholder="Ex: 42, 90+3"
                  />
                </div>
                <div className="form-actions">
                  <button onClick={handleCloseModal} className="cancel-button">Cancelar</button>
                  <button onClick={handleAddGoal} disabled={isSaving} className="submit-button">
                    {isSaving ? 'Salvando...' : 'Confirmar Gol'}
                  </button>
                </div>
              </>
            )}
            {modalState.type === 'card' && (
              <>
                <h4>Adicionar Cartão</h4>
                {modalState.player && <p><strong>Nome:</strong> {modalState.player.name} <small>({modalState.player.position})</small></p>}
                <div className="form-group">
                  <label>Tipo de Cartão:</label>
                  <select
                    value={modalInput.cardType}
                    onChange={e => setModalInput({ ...modalInput, cardType: e.target.value })}
                  >
                    <option value="yellow">Amarelo</option>
                    <option value="red">Vermelho Direto</option>
                    <option value="second_yellow">Segundo Amarelo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Minuto (opcional):</label>
                  <input
                    type="text"
                    value={modalInput.minute}
                    onChange={e => setModalInput({ ...modalInput, minute: e.target.value })}
                    placeholder="Ex: 78"
                  />
                </div>
                <div className="form-group">
                  <label>Motivo (opcional):</label>
                  <input
                    type="text"
                    value={modalInput.reason}
                    onChange={e => setModalInput({ ...modalInput, reason: e.target.value })}
                    placeholder="Ex: Reclamação"
                  />
                </div>
                <div className="form-actions">
                  <button onClick={handleCloseModal} className="cancel-button">Cancelar</button>
                  <button onClick={handleAddCard} disabled={isSaving} className="submit-button">
                    {isSaving ? 'Salvando...' : 'Confirmar Cartão'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetails;